import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { api, ErreurApi, type Identite } from "@/lib/api";

/**
 * L'identité côté navigateur.
 *
 * Elle sert UNIQUEMENT à décider quoi afficher. Toute autorisation réelle est
 * vérifiée côté serveur (article 15) : masquer un bouton n'est pas une
 * protection, c'est une politesse. Le périmètre des données, lui, n'est jamais
 * appliqué ici — il est appliqué dans les requêtes SQL (couture n°1).
 */

type EtatSession =
  | { statut: "chargement" }
  | { statut: "anonyme" }
  | { statut: "connecte"; identite: Identite };

type Contexte = EtatSession & {
  connexion: (email: string, motDePasse: string) => Promise<void>;
  deconnexion: () => Promise<void>;
  peut: (permission: string) => boolean;
};

const ContexteSession = createContext<Contexte | null>(null);

export function FournisseurSession({ children }: { children: ReactNode }) {
  const [etat, setEtat] = useState<EtatSession>({ statut: "chargement" });

  useEffect(() => {
    const controleur = new AbortController();
    api
      .moi(controleur.signal)
      .then((identite) => setEtat({ statut: "connecte", identite }))
      .catch((erreur) => {
        if (erreur instanceof DOMException && erreur.name === "AbortError") return;
        setEtat({ statut: "anonyme" });
      });
    return () => controleur.abort();
  }, []);

  const connexion = useCallback(async (email: string, motDePasse: string) => {
    const identite = await api.connexion(email, motDePasse);
    setEtat({ statut: "connecte", identite });
  }, []);

  const deconnexion = useCallback(async () => {
    try {
      await api.deconnexion();
    } catch (erreur) {
      // Une session déjà expirée côté serveur n'est pas une raison de rester
      // connecté côté écran.
      if (!(erreur instanceof ErreurApi)) throw erreur;
    }
    setEtat({ statut: "anonyme" });
  }, []);

  const peut = useCallback(
    (permission: string) =>
      etat.statut === "connecte" && etat.identite.permissions.includes(permission),
    [etat],
  );

  const valeur = useMemo(
    () => ({ ...etat, connexion, deconnexion, peut }),
    [etat, connexion, deconnexion, peut],
  );

  return <ContexteSession.Provider value={valeur}>{children}</ContexteSession.Provider>;
}

export function useSession(): Contexte {
  const contexte = useContext(ContexteSession);
  if (!contexte) throw new Error("useSession hors de FournisseurSession");
  return contexte;
}
